import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import { HiOutlineMail, HiOutlineLockClosed } from "react-icons/hi";
import SnackbarAlert from "../../common/sanckbar";
import { sendOtp, verifyOtp, resetPassword } from "../../../utils/api";
import "./forgot-password.scss";

export function ForgotPassword() {
  const [step, setStep] = useState<"email" | "otp" | "password">("email");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error">(
    "error"
  );
  const [snackbar, setSnackbar] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const showAlert = (message: string, severity: "success" | "error") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setSnackbar(true);
  };

  const closeSnackbar = () => setSnackbar(false);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes("@") || !email.includes(".")) {
      showAlert("Please enter a valid email address", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await sendOtp("sendOtp", { email });
      if (response && response.status === true) {
        showAlert("OTP sent successfully", "success");
        setStep("otp");
      } else {
        showAlert((response && response.message) || "Something went wrong", "error");
      }
    } catch (error) {
      showAlert("Failed to send OTP", "error");
      console.error("Send OTP error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otp.length < 4) {
      showAlert("OTP must be at least 4 digits", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await verifyOtp("verifyOtp", { email, otp });
      if (response && response.status === true) {
        showAlert("OTP verified successfully", "success");
        setStep("password");
      } else {
        showAlert((response && response.message) || "Invalid OTP", "error");
      }
    } catch (error) {
      showAlert("Invalid OTP or server error", "error");
      console.error("Verify OTP error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      showAlert("Password must be at least 6 characters", "error");
      return;
    }
    if (newPassword !== confirmPassword) {
      showAlert("Passwords do not match", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await resetPassword("resetPassword", {
        email,
        otp,
        password: newPassword,
      });
      if (response && response.status === true) {
        showAlert("Password reset successful!", "success");
        setTimeout(() => navigate("/login"), 1000);
      } else {
        showAlert((response && response.message) || "Something went wrong", "error");
      }
    } catch (error) {
      showAlert("Failed to reset password", "error");
      console.error("Reset password error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SnackbarAlert
        open={snackbar}
        closeSnackbar={closeSnackbar}
        message={alertMessage}
        severity={alertSeverity}
      />
      <div className="forgot-password-page">
        <div className="left-section">
          <div className="logo">
            <span className="logo-icon">DC</span>
          </div>
          <h1>Delnie Cloud</h1>
          <p>
            Efficiently manage master data with centralized control, role-level
            security, and seamless API creation. Integrate with MongoDB Charts
            for real-time analytics and customized insights, tailored to your
            business needs.
          </p>
        </div>

        <div className="right-section">
          <div className="portal-info">
            <span className="portal-icon">🖥️</span>
            <div>
              <p className="portal-title">ACCERON Client Portal</p>
              <p className="portal-subtitle">Powered by Delnie Cloud Admin</p>
            </div>
          </div>
          <h2>Forgot Password</h2>
          <p className="subtitle">
            {step === "email" && "Enter your email to receive an OTP"}
            {step === "otp" && "Enter the OTP sent to your email"}
            {step === "password" && "Set a new password"}
          </p>

          {step === "email" && (
            <form onSubmit={handleEmailSubmit}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <div className="input-wrapper">
                  <HiOutlineMail className="input-icon" />
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="sign-in-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ color: "#fff", marginRight: "8px" }}
                    />
                    Sending OTP...
                  </>
                ) : (
                  "Send OTP"
                )}
              </button>
            </form>
          )}

          {step === "otp" && (
            <form onSubmit={handleOtpSubmit}>
              <div className="form-group">
                <label htmlFor="otp">OTP</label>
                <div className="input-wrapper">
                  <HiOutlineLockClosed className="input-icon" />
                  <input
                    type="text"
                    id="otp"
                    inputMode="numeric"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    placeholder="••••"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="sign-in-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ color: "#fff", marginRight: "8px" }}
                    />
                    Verifying OTP...
                  </>
                ) : (
                  "Verify OTP"
                )}
              </button>
            </form>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group">
                <label htmlFor="new-password">New Password</label>
                <div className="input-wrapper">
                  <HiOutlineLockClosed className="input-icon" />
                  <input
                    type="password"
                    id="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="confirm-password">Confirm Password</label>
                <div className="input-wrapper">
                  <HiOutlineLockClosed className="input-icon" />
                  <input
                    type="password"
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    disabled={loading}
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="sign-in-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={20}
                      style={{ color: "#fff", marginRight: "8px" }}
                    />
                    Resetting...
                  </>
                ) : (
                  "Reset Password"
                )}
              </button>
            </form>
          )}

          <p className="create-account">
            Back to <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </>
  );
}

export default ForgotPassword;
